import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LockOutlined } from '@mui/icons-material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { ReactComponent as Logo } from 'images/logo.svg';
import routes from 'common/routes';

/**
 * Bloque "sección bloqueada" con el MISMO estilo premium que "completa tu perfil" (PremiumCard +
 * watermark del logo + ícono dorado + CTA blanco). Reutilizable en cualquier sección cerrada:
 * por defecto habla de adquirir un plan (Agenda, Mi Programa, etc.), pero `message`/`ctaLabel`/
 * `ctaTo` permiten explicar OTROS bloqueos — p. ej. a un contacto de empresa al que le retiraron
 * el permiso — en vez de redirigirlo en silencio, que es lo que hacía sentir la app rota.
 */
function LockedSectionBlock({ message = null, ctaLabel = null, ctaTo = null }) {
  const { t } = useTranslation(['common']);
  const theme = useTheme();

  const logoWatermarkStyles = {
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100%',
    opacity: 0.04,
    filter: theme.palette.brand.logoFilter,
    transform: 'translateX(34%)',
    pointerEvents: 'none'
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <PremiumCard
        sx={{ maxWidth: 460, width: '100%' }}
        innerSx={{ position: 'relative', overflow: 'hidden' }}
        padding={{ xs: 4, md: 5 }}>
        <Logo aria-hidden style={logoWatermarkStyles} />
        <Stack spacing={3} alignItems="center" sx={{ position: 'relative', textAlign: 'center' }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'gold.soft',
              border: '1px solid',
              borderColor: 'rgba(249, 189, 78, 0.25)'
            }}>
            <LockOutlined sx={{ fontSize: 38, color: 'gold.main' }} />
          </Box>
          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500, maxWidth: 320 }}>
            {message ||
              t('premium.locked', {
                defaultValue: 'Debes adquirir un plan para acceder a esta sección'
              })}
          </Typography>
          <Button
            component={RouterLink}
            to={ctaTo || routes.clientDashboard.path}
            fullWidth
            sx={{ ...premiumButtonSx, maxWidth: 280 }}>
            {ctaLabel || t('premium.viewPlans', { defaultValue: 'Ver planes' })}
          </Button>
        </Stack>
      </PremiumCard>
    </Box>
  );
}

LockedSectionBlock.propTypes = {
  ctaLabel: PropTypes.string,
  ctaTo: PropTypes.string,
  message: PropTypes.string
};

export default LockedSectionBlock;
